import React, { useState, useEffect, useRef } from 'react';

const IntelligentChange = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  const textCards = [
    {
      id: 1,
      title: 'Automating complex workflows',
      description: 'AI-driven automation that simplifies end-to-end processes, reduces manual effort, and improves operational efficiency across enterprise systems.'
    },
    {
      id: 2,
      title: 'AI-Driven Decision Intelligence',
      description: 'Enhancing human decision-making with AI by delivering real-time insights, predictive intelligence, and data-backed recommendations.'
    },
    {
      id: 3,
      title: 'Scalable Digital Ecosystems',
      description: 'Building secure, flexible, and future-ready digital platforms designed to scale with evolving business and technology needs.'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            // Show cards sequentially with 1.5 second delay
            textCards.forEach((_, index) => {
              setTimeout(() => {
                setVisibleCards(prev => [...prev, index]);
              }, index * 1500);
            });
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  return (
    <section ref={sectionRef} className="py-8 md:py-12 lg:py-16">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12 lg:mb-14">
          <h2 className="font-bold mb-4 md:mb-5">
            <span className="">Beyond Technology. Toward </span>
            <span className="secondary-text">Intelligent Change.</span>
          </h2>
          <p className="   max-w-4xl mx-auto px-4 leading-relaxed">
            Digital transformation at Avexa is not just about adopting new tools — it's about reimagining processes, 
            platforms, and decision-making using AI-driven intelligence.
          </p>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-6 lg:gap-8">
          {/* Card 1 - Text Card (Top Left) */}
          <div
            className={`primary-bg rounded-xl p-6 md:p-7 lg:p-8 shadow-lg flex flex-col justify-center h-[180px] md:h-[200px] transition-all duration-700 ${
              visibleCards.includes(0)
                ? 'opacity-100 scale-100'
                : 'opacity-0 scale-90'
            }`}
          >
            <h3 className="text-white   font-bold mb-3 md:mb-4 text-center">
              {textCards[0].title}
            </h3>
            <p className="text-white text-xs md: opacity-90 leading-relaxed text-center">
              {textCards[0].description}
            </p>
          </div>

          {/* Card 2 - Center Image (Top Center) */}
          <div className="relative overflow-hidden rounded-xl shadow-lg h-[180px] md:h-[200px]">
            <img
              src='/assets/digitalTransformation/3section-1.svg'
              alt="Business meeting with holographic display"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>

          {/* Card 3 - Text Card (Top Right) */}
          <div
            className={`primary-bg rounded-xl p-6 md:p-7 lg:p-8 shadow-lg flex flex-col justify-center h-[180px] md:h-[200px] transition-all duration-700 ${
              visibleCards.includes(1)
                ? 'opacity-100 scale-100'
                : 'opacity-0 scale-90'
            }`}
          >
            <h3 className="text-white   font-bold mb-3 md:mb-4 text-center">
              {textCards[1].title}
            </h3>
            <p className="text-white text-xs md: opacity-90 leading-relaxed text-center">
              {textCards[1].description}
            </p>
          </div>

          {/* Card 4 - AI Brain Image (Bottom Left) */}
          <div className="relative overflow-hidden rounded-xl shadow-lg h-[180px] md:h-[200px]">
            <img
              src='/assets/digitalTransformation/3section-2.svg'
              alt="Artificial Intelligence brain visualization"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>

          {/* Card 5 - Text Card (Bottom Center) */}
          <div
            className={`primary-bg rounded-xl p-6 md:p-7 lg:p-8 shadow-lg flex flex-col justify-center h-[180px] md:h-[200px] transition-all duration-700 ${
              visibleCards.includes(2)
                ? 'opacity-100 scale-100'
                : 'opacity-0 scale-90'
            }`}
          >
            <h3 className="text-white   font-bold mb-3 md:mb-4 text-center">
              {textCards[2].title}
            </h3>
            <p className="text-white text-xs md: opacity-90 leading-relaxed text-center">
              {textCards[2].description}
            </p>
          </div>

          {/* Card 6 - Cloud Ecosystem Image (Bottom Right) */}
          <div className="relative overflow-hidden rounded-xl shadow-lg h-[180px] md:h-[200px]">
            <img
              src='/assets/digitalTransformation/3section-3.svg'
              alt="Cloud ecosystem and digital infrastructure"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntelligentChange;